import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import artisticObjectsService from '../services/artisticObjectsService';
import {ArtisticObjectListing} from '../types/ArtisticObject';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import {handleError} from "../../../common/utils/errorHandling";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {Dropdown, DropdownChangeEvent} from "primereact/dropdown";
import placesService from "../services/placesService";
import {PlaceListing} from "../types/Place";
import AddToCollectionDialog from "./AddToCollectionDialog";
import {CONFIG} from "../../../common/config/settings";
import {useToast} from "../../../common/contexts/ToastContext";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";

const ArtisticObjectsList: React.FC = () => {
  const {t} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [artisticObjects, setArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const [places, setPlaces] = useState<PlaceListing[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    'place.name': {value: null, matchMode: FilterMatchMode.EQUALS}
  });
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [selectedArtisticObject, setSelectedArtisticObject] = useState<ArtisticObjectListing | null>(null);

  useEffect(() => {
    fetchArtisticObjects();
    fetchPlaces();
  }, []);

  const fetchArtisticObjects = async (): Promise<void> => {
    await handleError(
      async () => {
        const objectsData = await artisticObjectsService.getAllArtisticObjects();
        setArtisticObjects(objectsData);
      },
      t('admin.artisticObjects.errors.fetchingObjects')
    );
  };

  const fetchPlaces = async (): Promise<void> => {
    const placesData = await placesService.getAllPlaces();
    setPlaces(placesData);
  };

  const handleEdit = (artisticObject: ArtisticObjectListing) => {
    navigate(`/artistic-objects/${artisticObject.id}/edit`);
  };

  const handleDelete = async (artisticObject: ArtisticObjectListing) => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: artisticObject.name}),
      accept: async () => {
        await handleError(
          async () => {
            await artisticObjectsService.deleteArtisticObject(artisticObject.id);
            await fetchArtisticObjects();
          },
          t('admin.artisticObjects.errors.deleting'),
          undefined,
          showError
        );
      }
    });
  };

  const handleCreate = () => {
    navigate('/artistic-objects/new');
  };

  const handleAddToCollection = (artisticObject: ArtisticObjectListing) => {
    setSelectedArtisticObject(artisticObject);
    setDialogVisible(true);
  };

  const handleDialogHide = () => {
    setDialogVisible(false);
    setSelectedArtisticObject(null);
  };

  const placeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      // TODO use ids instead of names
      <Dropdown
        value={options.value}
        options={places}
        optionLabel="name"
        optionValue="name"
        placeholder={t('admin.artisticObjects.placeholders.searchByPlace')}
        onChange={(e: DropdownChangeEvent) => options.filterApplyCallback(e.value)}
      />
    );
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.artisticObjects.title.list')}
        subtitle={t('admin.artisticObjects.subtitle.list')}
      />

      <div className="mb-4">
        <AdminButton
          label={t('admin.artisticObjects.actions.createNew')}
          onClick={handleCreate}
          variant="black"
        />
      </div>

      <DataTable
        dataKey="id"
        value={artisticObjects}
        stripedRows
        paginator
        rows={CONFIG.admin.tables.rowsCount}
        filterDisplay="row"
        filters={filters}
        // columnResizeMode="fit"
        // resizableColumns
        showGridlines
      >
        <Column
          sortField="name"
          header={t('admin.common.columns.name')}
          sortable
          filter
          filterField="name"
          filterPlaceholder={t('admin.common.filters.searchByName')}
          showFilterMenu={false}
          showClearButton={false}
          // style={{ width: '700px', maxWidth: '700px' }}
          body={(obj: ArtisticObjectListing) => (
            <strong>{obj.name}</strong>
          )}
        />
        <Column
          header={t('admin.artisticObjects.fields.place')}
          sortable
          filter
          filterField="place.name"
          filterPlaceholder={t('admin.artisticObjects.placeholders.searchByPlace')}
          showFilterMenu={false}
          filterElement={placeFilterTemplate}
          body={(obj: ArtisticObjectListing) => (
            <span>{obj.place.name}</span>
          )}
        />
        <Column
          header={t('admin.common.columns.actions')}
          body={(obj: ArtisticObjectListing) => (
            <div>
              <Button
                onClick={() => handleAddToCollection(obj)}
                icon="fa fa-folder-plus"
                severity="success"
                size="small"
                outlined
                className="mr-2"
                aria-label={t('admin.artisticObjects.actions.addToCollection')}
                tooltip={t('admin.artisticObjects.actions.addToCollection')}
                tooltipOptions={{position: 'top'}}
              />
              <Button
                onClick={() => handleEdit(obj)}
                icon="fa fa-edit"
                severity="info"
                size="small"
                outlined
                className="mr-2"
                aria-label={t('admin.common.actions.edit')}
                tooltip={t('admin.common.actions.edit')}
                tooltipOptions={{position: 'top'}}
              />
              <Button
                onClick={() => handleDelete(obj)}
                icon="fa fa-trash"
                severity="danger"
                size="small"
                outlined
                aria-label={t('admin.common.actions.delete')}
                tooltip={t('admin.common.actions.delete')}
                tooltipOptions={{position: 'top'}}
              />
            </div>
          )}
        />
      </DataTable>

      {selectedArtisticObject && (
        <AddToCollectionDialog
          visible={dialogVisible}
          onHide={handleDialogHide}
          artisticObjectId={selectedArtisticObject.id}
          artisticObjectName={selectedArtisticObject.name}
        />
      )}
    </>
  );
};

export default ArtisticObjectsList;
